import axios from '../axios';

// ---------------------------------------------------------------------------
// Papéis (IAM) — service da fronteira (FC1 §A.7).
//
// Os endpoints de `/roles` são LEGADOS (Geração 1): respondem ora model cru,
// ora `{ roles: [...] }`, ora envelope — e o rótulo varia entre `name` e
// `nome` conforme a origem. TODOS os leitores daqui são defensivos de
// propósito (mesma postura de `inventory/pdvsApi.js`); quando o módulo de
// papéis for para o Gen3, só este arquivo muda.
//
// Consumidores: selects de papel (ListUsers/CreateUser/EditUser) e a tela
// "Gerenciar perfil de acesso" (pages/RoleShow, redesenho 2026-09-15).
// ---------------------------------------------------------------------------

// Mutação sai com `_skipForbiddenRedirect` (precedente de usersApi/transfersApi):
// um 403 numa escrita é divergência de grant no meio da sessão, e o redirect
// global recarregaria a página levando junto o diff de permissões não salvo.
const ACTION_CONFIG = { _skipForbiddenRedirect: true };

function unwrapBody(response) {
   const body = response?.data;
   // Gen3 futuro: { status, data: {...} }. Gen1: corpo cru.
   if (body && typeof body === 'object' && body.status === 'success') {
      return body.data ?? {};
   }
   return body ?? {};
}

/**
 * Catálogo de papéis. Consumido pelos selects das telas de USUÁRIO — que são
 * gateadas por `iam:user:*` e não por `iam:role:read`.
 *
 * Daí o `skipForbiddenRedirect` (2026-09-15): quem tem `iam:user:read` mas não
 * `iam:role:read` toma 403 aqui, e sem a flag o interceptor global mandaria a
 * página inteira para `/403` — a tela de usuários ficaria inacessível por causa
 * de um filtro acessório. Quem passa a flag assume degradar na própria UI.
 *
 * @param {object} [options]
 * @param {boolean} [options.skipForbiddenRedirect]
 */
export async function listRoles(options = {}) {
   const config = options.skipForbiddenRedirect ? { ...ACTION_CONFIG } : {};
   const response = await axios.get('/roles', config);
   const body = response?.data;

   const list = (() => {
      if (Array.isArray(body)) return body;
      if (Array.isArray(body?.roles)) return body.roles;
      if (Array.isArray(body?.data?.roles)) return body.data.roles;
      if (Array.isArray(body?.data)) return body.data;
      return [];
   })();

   return list.map((role) => ({
      ...role,
      id: role.id,
      name: role.name ?? role.nome ?? role.role_name ?? `Papel #${role.id}`,
   }));
}

// Shape normalizado da tela de gestão: `{ id, name, description,
// permissionIds }`. Desde o 0111 (§3.5) o backend manda `permission_ids`
// pronto; o fallback deriva do array de objetos (shape pré-deploy).
function normalizeRole(role) {
   if (!role || role.id == null) return null;
   const permissionIds = Array.isArray(role.permission_ids)
      ? role.permission_ids
      : (Array.isArray(role.permissions) ? role.permissions : [])
           .map((perm) => (typeof perm === 'object' ? perm.id : perm))
           .filter((permId) => permId != null);

   return {
      id: role.id,
      name: role.name ?? role.nome ?? role.role_name ?? `Papel #${role.id}`,
      description: role.description ?? role.descricao ?? '',
      permissionIds,
   };
}

/** Detalhe do papel (`GET /roles/:id` → `data.role`, 0111 §3.5). */
export async function getRole(roleId) {
   const response = await axios.get(`/roles/${roleId}`);
   const data = unwrapBody(response);
   return normalizeRole(data.role ?? data);
}

/**
 * Cria o papel e devolve `{ id }` para navegação à tela de gestão.
 * `permissionIds` é opcional — o fluxo canônico cria com zero permissões e
 * concede na matriz em seguida. Nome duplicado responde 409
 * `role_name_taken` com `field: "name"` (0111 §3.6) — o `ApiError` entrega
 * em `fieldErrors.name` e o form trata inline.
 */
export async function createRole({ name, description, permissionIds = [] }) {
   const payload = {
      name: name.trim(),
      description: description?.trim() || undefined,
      permission_ids: permissionIds,
   };
   const response = await axios.post('/roles', payload, ACTION_CONFIG);
   const data = unwrapBody(response);
   return normalizeRole(data.role ?? data) ?? {};
}

/**
 * Substituição TOTAL do conjunto de permissões do papel (não é diff):
 * `PUT /roles/:id/permissions { permission_ids }` — body estrito, array
 * vazio revoga tudo, id inexistente é 404 `permission_not_found` e NADA é
 * aplicado (0111 §3.7). Devolve o role completo da resposta — a tela troca o
 * baseline sem refetch. `null` no shape pré-deploy (sem corpo útil).
 */
export async function setRolePermissions(roleId, permissionIds) {
   const response = await axios.put(
      `/roles/${roleId}/permissions`,
      { permission_ids: permissionIds },
      ACTION_CONFIG
   );
   const data = unwrapBody(response);
   return normalizeRole(data.role ?? data);
}
